import { Chip } from '@issuemanager/apps/client/components';

import {
  useAtlasCoreThemeTokens,
  useAtlasSemanticThemeTokens,
} from '@issuemanager/apps/client/theme';
import { ActionStatus } from '@issuemanager/constants';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type ActionStatusData = {
  id: string;
  value: ActionStatus;
  label: string;
  color: string;
  backgroundColor: string;
  group: string;
};

export const useActionStatuses = (): ActionStatusData[] => {
  const { t } = useTranslation(['common', 'action']);
  const semantic = useAtlasSemanticThemeTokens();
  const core = useAtlasCoreThemeTokens();

  const whiteColor = core.color.legacy.white.value;
  const darkColor = semantic.color.text.body.value;

  const actionStatuses: ActionStatusData[] = useMemo(
    () => [
      {
        id: '1',
        value: ActionStatus.DRAFT,
        label: t('action:view.details.status.draft'),
        backgroundColor: semantic.color.accent['05'].value,
        color: darkColor,
        group: t('statusLabels.action'),
      },
      {
        id: '2',
        value: ActionStatus.PENDING,
        label: t('action:view.details.status.pending'),
        backgroundColor: semantic.color.accent['04'].value,
        color: darkColor,
        group: t('statusLabels.action'),
      },
      {
        id: '3',
        value: ActionStatus.OPEN,
        label: t('action:view.details.status.open'),
        backgroundColor: semantic.color.status.warning.value,
        color: darkColor,
        group: t('statusLabels.action'),
      },
      {
        id: '4',
        value: ActionStatus.IN_PROGRESS,
        label: t('action:view.details.status.inProgress'),
        backgroundColor: semantic.color.link.default.value,
        color: whiteColor,
        group: t('statusLabels.action'),
      },
      {
        id: '5',
        value: ActionStatus.COMPLETED,
        label: t('action:view.details.status.completed'),
        backgroundColor: semantic.color.status.success.value,
        color: whiteColor,
        group: t('statusLabels.action'),
      },
      {
        id: '6',
        value: ActionStatus.TO_BE_APPROVED,
        label: t('action:view.details.status.toBeApproved'),
        backgroundColor: semantic.color.link.default.value,
        color: whiteColor,
        group: t('statusLabels.action'),
      },
      {
        id: '7',
        value: ActionStatus.DISCARDED,
        label: t('action:view.details.status.discarded'),
        backgroundColor: core.color.legacy.dark.value,
        color: whiteColor,
        group: t('statusLabels.action'),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [semantic, core, darkColor, whiteColor],
  );

  return actionStatuses;
};

interface Props {
  status: ActionStatus;
}

export function ActionStatusChip({ status }: Props): React.JSX.Element | null {
  const semantic = useAtlasSemanticThemeTokens();
  const actionStatuses = useActionStatuses();
  const actionStatus = actionStatuses.find((s) => s.value === status);

  if (!status || !actionStatus) {
    return null;
  }

  return (
    <Chip
      data-testid='status'
      label={actionStatus.label.toUpperCase()}
      title={status}
      sx={{
        fontWeight: semantic.fontWeight.emphasis.value,
        backgroundColor: actionStatus.backgroundColor,
        color: actionStatus.color,
        borderRadius: semantic.spacing.sm.value,
        padding: semantic.spacing.default.value,
        letterSpacing: '1px',
      }}
    />
  );
}
